import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../../components/ui/dialog';
import { Gift, Plus, Trash2, Pencil, Link2, RefreshCw, ExternalLink, Users } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY_HOLIDAY = { name: '', date: '', paid_hours: 8, active: true, repeats_annually: true, eligibility_scope: 'all', eligible_departments: [], eligible_employee_ids: [], notes: '' };
const EMPTY_LINK = { display_name: '', url: '', description: '', category: '401k', active: true, display_order: 0 };
const LINK_CATEGORIES = ['401k', 'health', 'dental', 'vision', 'handbook', 'documents', 'other'];

const fmtDate = (s) => { try { return new Date(`${String(s).slice(0, 10)}T00:00:00`).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }); } catch { return s; } };

const HolidaysBenefits = () => {
  const [holidays, setHolidays] = useState([]);
  const [links, setLinks] = useState([]);
  const [hDialog, setHDialog] = useState(false);
  const [lDialog, setLDialog] = useState(false);
  const [hForm, setHForm] = useState(EMPTY_HOLIDAY);
  const [lForm, setLForm] = useState(EMPTY_LINK);
  const [editingH, setEditingH] = useState(null);
  const [editingL, setEditingL] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const [h, l] = await Promise.all([
        axios.get(`${API}/admin/holidays`, { withCredentials: true }),
        axios.get(`${API}/admin/benefit-links`, { withCredentials: true }),
      ]);
      setHolidays(h.data); setLinks(l.data);
    } catch { toast.error('Failed to load holidays / benefits'); }
  };
  useEffect(() => { load(); }, []);

  const openHoliday = (h) => { setEditingH(h?.id || null); setHForm(h ? { ...EMPTY_HOLIDAY, ...h } : EMPTY_HOLIDAY); setHDialog(true); };
  const openLink = (l) => { setEditingL(l?.id || null); setLForm(l ? { ...EMPTY_LINK, ...l } : EMPTY_LINK); setLDialog(true); };

  const saveHoliday = async () => {
    if (!hForm.name || !hForm.date) { toast.error('Name and date are required'); return; }
    setSaving(true);
    try {
      const body = { ...hForm, paid_hours: Number(hForm.paid_hours || 0) };
      if (editingH) await axios.put(`${API}/admin/holidays/${editingH}`, body, { withCredentials: true });
      else await axios.post(`${API}/admin/holidays`, body, { withCredentials: true });
      toast.success('Holiday saved · payroll credits synced');
      setHDialog(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    setSaving(false);
  };

  const deleteHoliday = async (id) => {
    if (!window.confirm('Delete this holiday? Future paid-holiday credits will be removed (past payroll is preserved).')) return;
    try { await axios.delete(`${API}/admin/holidays/${id}`, { withCredentials: true }); toast.success('Holiday deleted'); load(); }
    catch { toast.error('Delete failed'); }
  };

  const syncAll = async () => {
    try { const { data } = await axios.post(`${API}/admin/holidays/sync`, {}, { withCredentials: true }); toast.success(`Synced ${data.synced} holiday(s)`); load(); }
    catch { toast.error('Sync failed'); }
  };

  const saveLink = async () => {
    if (!lForm.display_name || !lForm.url) { toast.error('Name and URL are required'); return; }
    setSaving(true);
    try {
      const body = { ...lForm, display_order: Number(lForm.display_order || 0) };
      if (editingL) await axios.put(`${API}/admin/benefit-links/${editingL}`, body, { withCredentials: true });
      else await axios.post(`${API}/admin/benefit-links`, body, { withCredentials: true });
      toast.success('Benefit link saved');
      setLDialog(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    setSaving(false);
  };

  const deleteLink = async (id) => {
    if (!window.confirm('Delete this benefit link?')) return;
    try { await axios.delete(`${API}/admin/benefit-links/${id}`, { withCredentials: true }); toast.success('Link deleted'); load(); }
    catch { toast.error('Delete failed'); }
  };

  return (
    <div data-testid="holidays-benefits-page" className="space-y-6">
      {/* Paid Holidays */}
      <div className="bg-[#141414] border border-[#262626] rounded-xl p-4 sm:p-5">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
          <div className="flex items-center gap-2"><Gift className="w-5 h-5 text-[#D4AF37]" /><h2 className="text-white font-semibold text-lg">Paid Holidays</h2></div>
          <div className="flex gap-2">
            <Button data-testid="holiday-sync-btn" variant="outline" onClick={syncAll} className="border-[#262626] text-white"><RefreshCw className="w-4 h-4 mr-1" /> Sync</Button>
            <Button data-testid="holiday-add-btn" onClick={() => openHoliday(null)} className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB]"><Plus className="w-4 h-4 mr-1" /> Add Holiday</Button>
          </div>
        </div>
        {holidays.length === 0 ? (
          <p className="text-sm text-[#A3A3A3]">No paid holidays configured yet.</p>
        ) : (
          <div className="space-y-2">
            {holidays.map((h) => (
              <div key={h.id} data-testid={`holiday-row-${h.id}`} className="flex items-center justify-between gap-3 rounded-lg border border-[#262626] bg-[#0F0F0F] p-3 flex-wrap">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-white font-medium">{h.name}</span>
                    {!h.active && <span className="text-[10px] uppercase bg-[#333] text-[#A3A3A3] px-2 py-0.5 rounded">Inactive</span>}
                    {h.repeats_annually && <span className="text-[10px] uppercase bg-[#D4AF37]/15 text-[#D4AF37] px-2 py-0.5 rounded">Annual</span>}
                  </div>
                  <p className="text-xs text-[#A3A3A3]">{fmtDate(h.date)} · {h.paid_hours}h paid · scope: {h.eligibility_scope}</p>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-xs text-[#A3A3A3] flex items-center gap-1" title="Upcoming recipients"><Users className="w-3 h-3" /> {h.upcoming_recipients ?? 0}</span>
                  <button data-testid={`holiday-edit-${h.id}`} onClick={() => openHoliday(h)} className="text-[#A3A3A3] hover:text-white"><Pencil className="w-4 h-4" /></button>
                  <button data-testid={`holiday-delete-${h.id}`} onClick={() => deleteHoliday(h.id)} className="text-red-400 hover:text-red-300"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Benefit Links */}
      <div className="bg-[#141414] border border-[#262626] rounded-xl p-4 sm:p-5">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
          <div className="flex items-center gap-2"><Link2 className="w-5 h-5 text-[#D4AF37]" /><h2 className="text-white font-semibold text-lg">Benefit Links</h2></div>
          <Button data-testid="link-add-btn" onClick={() => openLink(null)} className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB]"><Plus className="w-4 h-4 mr-1" /> Add Link</Button>
        </div>
        {links.length === 0 ? (
          <p className="text-sm text-[#A3A3A3]">No benefit links configured yet.</p>
        ) : (
          <div className="space-y-2">
            {links.map((l) => (
              <div key={l.id} data-testid={`link-row-${l.id}`} className="flex items-center justify-between gap-3 rounded-lg border border-[#262626] bg-[#0F0F0F] p-3 flex-wrap">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-white font-medium">{l.display_name}</span>
                    <span className="text-[10px] uppercase bg-[#262626] text-[#A3A3A3] px-2 py-0.5 rounded">{l.category}</span>
                    {!l.active && <span className="text-[10px] uppercase bg-[#333] text-[#A3A3A3] px-2 py-0.5 rounded">Inactive</span>}
                  </div>
                  <a href={l.url} target="_blank" rel="noopener noreferrer" className="text-xs text-[#D4AF37] hover:underline inline-flex items-center gap-1">{l.url} <ExternalLink className="w-3 h-3" /></a>
                </div>
                <div className="flex items-center gap-3">
                  <button data-testid={`link-edit-${l.id}`} onClick={() => openLink(l)} className="text-[#A3A3A3] hover:text-white"><Pencil className="w-4 h-4" /></button>
                  <button data-testid={`link-delete-${l.id}`} onClick={() => deleteLink(l.id)} className="text-red-400 hover:text-red-300"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Holiday dialog */}
      <Dialog open={hDialog} onOpenChange={setHDialog}>
        <DialogContent data-testid="holiday-dialog" className="bg-[#141414] border-[#262626] max-w-lg">
          <DialogHeader><DialogTitle className="text-white">{editingH ? 'Edit Holiday' : 'Add Holiday'}</DialogTitle>
            <DialogDescription className="text-[#A3A3A3]">Set the holiday date, paid hours, and who is eligible. Eligible employees are auto-credited on payroll.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-white text-xs">Holiday Name</Label><Input data-testid="holiday-name" value={hForm.name} onChange={(e) => setHForm({ ...hForm, name: e.target.value })} className="bg-[#0A0A0A] border-[#262626] text-white" placeholder="Thanksgiving" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-white text-xs">Date</Label><Input data-testid="holiday-date" type="date" value={(hForm.date || '').slice(0, 10)} onChange={(e) => setHForm({ ...hForm, date: e.target.value })} className="bg-[#0A0A0A] border-[#262626] text-white" /></div>
              <div><Label className="text-white text-xs">Paid Hours</Label><Input data-testid="holiday-hours" type="number" step="0.5" value={hForm.paid_hours} onChange={(e) => setHForm({ ...hForm, paid_hours: e.target.value })} className="bg-[#0A0A0A] border-[#262626] text-white" /></div>
            </div>
            <div>
              <Label className="text-white text-xs">Eligible Employees</Label>
              <Select value={hForm.eligibility_scope} onValueChange={(v) => setHForm({ ...hForm, eligibility_scope: v })}>
                <SelectTrigger data-testid="holiday-scope" className="bg-[#0A0A0A] border-[#262626] text-white"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#141414] border-[#262626]">
                  <SelectItem value="all" className="text-white">All eligible employees</SelectItem>
                  <SelectItem value="departments" className="text-white">Specific departments</SelectItem>
                  <SelectItem value="employees" className="text-white">Specific employees</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {hForm.eligibility_scope === 'departments' && (
              <div><Label className="text-white text-xs">Departments (comma-separated)</Label><Input data-testid="holiday-departments" value={(hForm.eligible_departments || []).join(', ')} onChange={(e) => setHForm({ ...hForm, eligible_departments: e.target.value.split(',').map((s) => s.trim()).filter(Boolean) })} className="bg-[#0A0A0A] border-[#262626] text-white" placeholder="service, construction" /></div>
            )}
            {hForm.eligibility_scope === 'employees' && (
              <div><Label className="text-white text-xs">Employee IDs (comma-separated)</Label><Input data-testid="holiday-employees" value={(hForm.eligible_employee_ids || []).join(', ')} onChange={(e) => setHForm({ ...hForm, eligible_employee_ids: e.target.value.split(',').map((s) => s.trim()).filter(Boolean) })} className="bg-[#0A0A0A] border-[#262626] text-white" /></div>
            )}
            <div><Label className="text-white text-xs">Notes (internal)</Label><Textarea data-testid="holiday-notes" value={hForm.notes || ''} onChange={(e) => setHForm({ ...hForm, notes: e.target.value })} rows={2} className="bg-[#0A0A0A] border-[#262626] text-white" /></div>
            <div className="flex gap-4">
              <label className="flex items-center gap-2 text-white text-sm cursor-pointer" data-testid="holiday-active"><input type="checkbox" checked={hForm.active} onChange={(e) => setHForm({ ...hForm, active: e.target.checked })} /> Active</label>
              <label className="flex items-center gap-2 text-white text-sm cursor-pointer" data-testid="holiday-repeats"><input type="checkbox" checked={hForm.repeats_annually} onChange={(e) => setHForm({ ...hForm, repeats_annually: e.target.checked })} /> Repeats annually</label>
            </div>
            <div className="flex gap-2 pt-2">
              <Button data-testid="holiday-save" onClick={saveHoliday} disabled={saving} className="flex-1 bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB]">{saving ? 'Saving…' : 'Save Holiday'}</Button>
              <Button variant="outline" onClick={() => setHDialog(false)} className="border-[#262626] text-[#A3A3A3]">Cancel</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Benefit link dialog */}
      <Dialog open={lDialog} onOpenChange={setLDialog}>
        <DialogContent data-testid="link-dialog" className="bg-[#141414] border-[#262626] max-w-lg">
          <DialogHeader><DialogTitle className="text-white">{editingL ? 'Edit Benefit Link' : 'Add Benefit Link'}</DialogTitle>
            <DialogDescription className="text-[#A3A3A3]">Add a resource link employees can open from their My Benefits page. Inactive links are hidden.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-white text-xs">Display Name</Label><Input data-testid="link-name" value={lForm.display_name} onChange={(e) => setLForm({ ...lForm, display_name: e.target.value })} className="bg-[#0A0A0A] border-[#262626] text-white" placeholder="401(k) Portal" /></div>
            <div><Label className="text-white text-xs">URL</Label><Input data-testid="link-url" value={lForm.url} onChange={(e) => setLForm({ ...lForm, url: e.target.value })} className="bg-[#0A0A0A] border-[#262626] text-white" placeholder="https://..." /></div>
            <div><Label className="text-white text-xs">Description</Label><Input data-testid="link-desc" value={lForm.description || ''} onChange={(e) => setLForm({ ...lForm, description: e.target.value })} className="bg-[#0A0A0A] border-[#262626] text-white" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-white text-xs">Category</Label>
                <Select value={lForm.category} onValueChange={(v) => setLForm({ ...lForm, category: v })}>
                  <SelectTrigger data-testid="link-category" className="bg-[#0A0A0A] border-[#262626] text-white"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#141414] border-[#262626]">
                    {LINK_CATEGORIES.map((c) => <SelectItem key={c} value={c} className="text-white capitalize">{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label className="text-white text-xs">Display Order</Label><Input data-testid="link-order" type="number" value={lForm.display_order} onChange={(e) => setLForm({ ...lForm, display_order: e.target.value })} className="bg-[#0A0A0A] border-[#262626] text-white" /></div>
            </div>
            <label className="flex items-center gap-2 text-white text-sm cursor-pointer" data-testid="link-active"><input type="checkbox" checked={lForm.active} onChange={(e) => setLForm({ ...lForm, active: e.target.checked })} /> Active (visible to employees)</label>
            <div className="flex gap-2 pt-2">
              <Button data-testid="link-save" onClick={saveLink} disabled={saving} className="flex-1 bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB]">{saving ? 'Saving…' : 'Save Link'}</Button>
              <Button variant="outline" onClick={() => setLDialog(false)} className="border-[#262626] text-[#A3A3A3]">Cancel</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default HolidaysBenefits;
